import { z } from 'zod';

/**
 * Esquema Zod para validar coordenadas geográficas
 */
export const CoordenadasSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180)
}).nullable();

/**
 * Esquema Zod para validar un Productor
 */
export const ProductorSchema = z.object({
  id_productor: z.string().uuid('El id_productor debe ser un UUID válido'),
  nombre: z.string().min(1),
  unidad_operativa: z.string().nullable(),
  comuna: z.string().nullable(),
  email: z.string().email().nullable().or(z.literal('')),
  ultima_visita: z.string().datetime().nullable(),
  coordenadas: CoordenadasSchema
});

/**
 * Esquema Zod para validar una Visita
 */
export const VisitaSchema = z.object({
  id_visita: z.string().uuid('El id_visita debe ser un UUID válido'),
  id_usuario: z.string().min(1),
  usuario: z.string().min(1),
  unidad_operativa: z.string().min(1),
  fecha: z.string().datetime('La fecha debe estar en formato ISO 8601'),
  lugar: z.string().min(1),
  estado: z.string().min(1),
  tipo: z.string().min(1),
  productor: ProductorSchema,
  // Campos calculados para compatibilidad
  latitud: z.number().min(-90).max(90).default(0),
  longitud: z.number().min(-180).max(180).default(0)
});

/**
 * Esquema para validar parámetros de consulta de visitas
 */
export const VisitasQueryParamsSchema = z.object({
  desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha debe ser yyyy-mm-dd').optional(),
  hasta: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha debe ser yyyy-mm-dd').optional(),
  id_estado_visita: z.number().int().positive().optional(),
  id_tipo_visita: z.number().int().positive().optional(),
  es_parcial: z.boolean().optional(),
  page: z.number().int().positive().min(1).default(1).optional(),
  size: z.number().int().positive().min(1).default(5).optional(),
  sort: z.string().default('fecha').optional(),
  order: z.enum(['asc', 'desc']).default('asc').optional(),
  search: z.string().optional()
});

/**
 * Esquema para validar un Estado de Visita
 */
export const EstadoVisitaSchema = z.object({
  id_estado_visita: z.number().int().positive(),
  descripcion: z.string().min(1)
});

/**
 * Esquema para validar un Tipo de Visita
 */
export const TipoVisitaSchema = z.object({
  id_tipo_visita: z.number().int().positive(),
  descripcion: z.string().min(1)
});

export const IdObjetivosVisitaRequestSchema = z.array(z.number()).nullish();

/**
 * Esquema para validar la creación de una nueva visita.
 * Basado en CrearVisitaRequest del OpenAPI: POST /api/v1/visita/crear (application/json).
 * Campos requeridos: id_productor, fecha, lugar, id_tipo_visita, comentarios, es_contingencia, bolsa_visita.
 */
export const CrearVisitaRequestSchema = z.object({
  id_productor: z.string().uuid('El id_productor debe ser un UUID válido'),
  fecha: z.string().datetime('La fecha debe estar en formato ISO 8601'),
  lugar: z.string().min(1, 'El lugar es requerido'),
  id_tipo_visita: z.number().int().positive('El tipo de visita es requerido'),
  comentarios: z.string(),
  es_contingencia: z.boolean(),
  bolsa_visita: z.boolean(),
  descripcion: z.string().min(1, 'La descripción es requerida'),
  descripcion_emergencia: z.string().nullable().optional(),
  id_objetivo_visita: IdObjetivosVisitaRequestSchema,
  objetivo_otro: z.string().nullable().optional(),
  id_plan_negocio: z.string().uuid().nullable().optional()
});

/**
 * Esquema para validar el reagendamiento de una visita.
 * Alineado con AgendarVisitaRequest del OpenAPI: POST /api/v1/visita/{id_visita}/agendar.
 * Campos: fecha (required), lugar, id_tipo_visita, es_contingencia, bolsa_visita.
 */
export const AgendarVisitaRequestSchema = z.object({
  fecha: z.string().datetime('La fecha debe estar en formato ISO 8601'),
  lugar: z.string().nullable().optional(),
  id_tipo_visita: z.number().int().nullable().optional(),
  es_contingencia: z.boolean().nullable().optional().default(false),
  bolsa_visita: z.boolean().default(false),
  descripcion: z.string().min(1, 'La descripción es requerida'),
  descripcion_emergencia: z.string().nullable().optional(),
  id_objetivo_visita: IdObjetivosVisitaRequestSchema,
  objetivo_otro: z.string().nullable().optional()
});

/**
 * Tipos TypeScript inferidos
 */
export type VisitaValidated = z.infer<typeof VisitaSchema>;
export type ProductorValidated = z.infer<typeof ProductorSchema>;
export type VisitasQueryParamsValidated = z.infer<typeof VisitasQueryParamsSchema>;
export type EstadoVisitaValidated = z.infer<typeof EstadoVisitaSchema>;
export type TipoVisitaValidated = z.infer<typeof TipoVisitaSchema>;
export type CrearVisitaRequestValidated = z.infer<typeof CrearVisitaRequestSchema>;
export type AgendarVisitaRequestValidated = z.infer<typeof AgendarVisitaRequestSchema>;
